import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface VisitorStats {
  visitsLast24Hours: number;
  visitsLastWeek: number;
  uniqueVisitors: number;
  activeUsers: number;
}

export function useVisitorTracking(): void {
  const heartbeatIntervalRef = useRef<number | null>(null);
  const visitorIdRef = useRef<string | null>(null);
  const visibilityTimeoutRef = useRef<number | null>(null);
  const hasTrackedRef = useRef<boolean>(false); // Prevent duplicate tracking (React StrictMode)

  useEffect(() => {
    // Mark inactive when tab becomes hidden (user switches tabs or minimizes)
    const handleVisibilityChange = (): void => {
      if (document.hidden && visitorIdRef.current) {
        // Tab is hidden - mark as inactive after a delay
        if (visibilityTimeoutRef.current) {
          clearTimeout(visibilityTimeoutRef.current);
        }
        visibilityTimeoutRef.current = window.setTimeout(() => {
          if (document.hidden && visitorIdRef.current) {
            void Promise.resolve(
              supabase
                .from('visitors')
                .update({ is_active: false })
                .eq('visitor_id', visitorIdRef.current)
            ).catch(() => {
              // Silently fail
            });
          }
        }, 30000); // 30 seconds after tab hidden
      } else if (!document.hidden && visitorIdRef.current) {
        // Tab is visible again - mark as active
        if (visibilityTimeoutRef.current) {
          clearTimeout(visibilityTimeoutRef.current);
          visibilityTimeoutRef.current = null;
        }
        void Promise.resolve(
          supabase
            .from('visitors')
            .update({
              last_active_at: new Date().toISOString(),
              is_active: true
            })
            .eq('visitor_id', visitorIdRef.current)
        ).catch(() => {
          // Silently fail
        });
      }
    };

    const trackVisit = async (): Promise<void> => {
      try {
        // Prevent duplicate tracking (React StrictMode double-mounts in development)
        if (hasTrackedRef.current) {
          return;
        }
        hasTrackedRef.current = true;

        // Check if Supabase is configured
        if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
          return;
        }

        // Generate or get visitor ID from localStorage
        const VISITOR_ID_KEY = 'site_visitor_id';
        let visitorId = localStorage.getItem(VISITOR_ID_KEY);
        
        if (!visitorId) {
          visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem(VISITOR_ID_KEY, visitorId);
        }
        
        visitorIdRef.current = visitorId;

        // Get user agent and other info
        const userAgent = navigator.userAgent;
        const referrer = document.referrer || 'direct';
        const timestamp = new Date().toISOString();
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

        // UPSERT: Insert new visitor OR update existing visitor's timestamp
        // This ensures ONE row per unique visitor
        const { error: upsertError } = await supabase
          .from('visitors')
          .upsert({
            visitor_id: visitorId,
            user_agent: userAgent,
            referrer: referrer,
            visited_at: timestamp,
            visit_date: today,
            last_active_at: timestamp,
            is_active: true
          }, {
            onConflict: 'visitor_id',
            ignoreDuplicates: false // Update existing record
          });

        if (upsertError && upsertError.code !== '42P01') {
          console.warn('Failed to upsert visitor:', upsertError);
        }

        // Set up heartbeat to update last_active_at every 20 seconds for more frequent updates
        heartbeatIntervalRef.current = window.setInterval(async () => {
          if (visitorIdRef.current && !document.hidden) {
            try {
              const now = new Date().toISOString();
              await supabase
                .from('visitors')
                .update({
                  last_active_at: now,
                  is_active: true
                })
                .eq('visitor_id', visitorIdRef.current);
            } catch (error) {
              // Silently fail
            }
          } else if (visitorIdRef.current && document.hidden) {
            // If tab is hidden, mark as inactive
            try {
              await supabase
                .from('visitors')
                .update({ is_active: false })
                .eq('visitor_id', visitorIdRef.current);
            } catch (error) {
              // Silently fail
            }
          }
        }, 20 * 1000); // 20 seconds

      } catch (error) {
        // Silently fail - visitor tracking shouldn't break the site
        console.warn('Visitor tracking error:', error);
      }
    };

    // Track visit after a short delay to ensure page is loaded
    const timeoutId = window.setTimeout(() => {
      void trackVisit();
    }, 1000);

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Mark as inactive when user leaves - use sendBeacon for reliability
    const handleBeforeUnload = (): void => {
      if (visitorIdRef.current) {
        const visitorId = visitorIdRef.current;
        
        // Use sendBeacon (synchronous, works even when page is unloading)
        if (navigator.sendBeacon && process.env.REACT_APP_SUPABASE_URL) {
          // Create a FormData to send via POST
          const formData = new FormData();
          formData.append('visitor_id', visitorId);
          formData.append('is_active', 'false');
          
          // Try to use the REST API endpoint
          const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/visitors?visitor_id=eq.${visitorId}`;
          navigator.sendBeacon(url, formData);
        }
        
        // Also try direct update (non-blocking)
        void Promise.resolve(
          supabase
            .from('visitors')
            .update({ is_active: false })
            .eq('visitor_id', visitorId)
        ).catch(() => {
          // Silently fail
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearTimeout(timeoutId);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Mark as inactive on cleanup - use sendBeacon for reliability
      if (visitorIdRef.current) {
        const visitorId = visitorIdRef.current;
        
        // Try sendBeacon first (more reliable on page unload)
        if (navigator.sendBeacon && process.env.REACT_APP_SUPABASE_URL) {
          const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/visitors?visitor_id=eq.${visitorId}`;
          const data = JSON.stringify({ is_active: false });
          navigator.sendBeacon(url, data);
        } else {
          // Fallback to regular update
          void Promise.resolve(
            supabase
              .from('visitors')
              .update({ is_active: false })
              .eq('visitor_id', visitorId)
          ).catch(() => {
            // Silently fail
          });
        }
      }
    };
  }, []);
}

export async function getVisitorStats(): Promise<VisitorStats> {
  try {
    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
      return { visitsLast24Hours: 0, visitsLastWeek: 0, uniqueVisitors: 0, activeUsers: 0 };
    }

    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const sixtySecondsAgo = new Date(now.getTime() - 60 * 1000).toISOString();

    // Get all visits with their timestamps and visitor IDs
    const { data: allVisits } = await supabase
      .from('visitors')
      .select('visitor_id, visited_at');

    // Calculate UNIQUE visitors for each time period
    const uniqueIdsLast24Hours = new Set<string>();
    const uniqueIdsLastWeek = new Set<string>();
    const uniqueIdsAllTime = new Set<string>();

    if (allVisits) {
      for (const visit of allVisits) {
        const visitedAt = (visit as { visited_at: string }).visited_at;
        const visitorId = (visit as { visitor_id: string }).visitor_id;
        
        // Count unique visitors for all time
        uniqueIdsAllTime.add(visitorId);
        
        // Count unique visitors in last 24 hours
        if (visitedAt >= twentyFourHoursAgo) {
          uniqueIdsLast24Hours.add(visitorId);
        }
        
        // Count unique visitors in last 7 days
        if (visitedAt >= sevenDaysAgo) {
          uniqueIdsLastWeek.add(visitorId);
        }
      }
    }

    const visitsLast24Hours = uniqueIdsLast24Hours.size;
    const visitsLastWeek = uniqueIdsLastWeek.size;
    const uniqueVisitors = uniqueIdsAllTime.size;

    // Mark old visits as inactive (cleanup)
    await supabase
      .from('visitors')
      .update({ is_active: false })
      .lt('last_active_at', sixtySecondsAgo);

    // Count active users (distinct visitor_ids active in last 60 seconds)
    const { data: activeData } = await supabase
      .from('visitors')
      .select('visitor_id')
      .gte('last_active_at', sixtySecondsAgo)
      .eq('is_active', true);
    
    let activeUsers = 0;
    if (activeData) {
      const activeIds = new Set(activeData.map((v: { visitor_id: string }) => v.visitor_id));
      activeUsers = activeIds.size;
    }

    return {
      visitsLast24Hours,
      visitsLastWeek,
      uniqueVisitors,
      activeUsers
    };
  } catch (error) {
    console.warn('Failed to get visitor stats:', error);
    return { visitsLast24Hours: 0, visitsLastWeek: 0, uniqueVisitors: 0, activeUsers: 0 };
  }
}

